import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class AkStickyAtc extends HTMLElement {
  #abortController = new AbortController();
  #observer;
  #mainButton;
  #footerSpacer;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const selected = document.querySelector('.product-information [data-ak-selected-variant]');
    const select = this.querySelector('[data-ak-sticky-variant-select]');
    if (selected && select) {
      try {
        const variant = JSON.parse(selected.textContent);
        if (variant?.id) {
          let option = Array.from(select.options).find(option => option.value === String(variant.id));
          if (!option) {
            option = new Option(variant.public_title || variant.title, String(variant.id));
            option.dataset.available = String(variant.available);
            option.dataset.price = String(variant.price);
            select.add(option);
          }
          select.value = String(variant.id);
        }
      } catch {}
    }
    const signal = this.#abortController.signal;
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.querySelector('[data-ak-sticky-submit]')?.addEventListener('click', this.#submitMainForm, { signal });
    this.querySelector('[data-ak-sticky-variant-select]')?.addEventListener('change', this.#handleStickyVariant, { signal });
    this.addEventListener('click', this.#handleQuantityClick, { signal });

    this.#mainButton = document.querySelector('.product-information .add-to-cart-button');
    if (this.#mainButton) {
      this.#observer = new IntersectionObserver(
        this.#syncVisibility,
        { threshold: [0, 1] }
      );
      this.#observer.observe(this.#mainButton);
      window.addEventListener('scroll', this.#syncVisibility, { passive: true, signal });
      window.addEventListener('resize', this.#syncVisibility, { passive: true, signal });
      this.#syncVisibility();
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#footerSpacer?.remove();
    document.documentElement.style.removeProperty('--ak-sticky-atc-height');
  }

  #submitMainForm = () => {
    const form = document.querySelector('.product-information product-form-component form');
    const stickyVariant = this.querySelector('[data-ak-sticky-variant-select]');
    const mainVariant = form?.querySelector('[name="id"]');
    const button = this.querySelector('[data-ak-sticky-submit]');
    const mainButton = form?.querySelector('[type="submit"]');
    const variantId = stickyVariant?.value || mainVariant?.value;
    if (!form || !button || !mainButton || !variantId || button.disabled || mainVariant?.disabled) return;
    mainVariant.value = variantId;
    const mainQuantity = form.querySelector('[name="quantity"]');
    if (mainQuantity) mainQuantity.value = String(Math.max(1, Number(this.querySelector('[data-ak-quantity]')?.value) || 1));
    mainButton.click();
  };

  #changeQuantity = (change) => {
    const input = this.querySelector('[data-ak-quantity]');
    if (!input) return;
    input.value = Math.max(Number(input.min) || 1, (Number(input.value) || 1) + change);
  };

  #handleQuantityClick = (event) => {
    if (event.target.closest('[data-ak-quantity-minus]')) this.#changeQuantity(-1);
    if (event.target.closest('[data-ak-quantity-plus]')) this.#changeQuantity(1);
  };

  #handleStickyVariant = (event) => {
    const option = event.target.selectedOptions?.[0];
    const button = this.querySelector('[data-ak-sticky-submit]');
    const label = this.querySelector('[data-ak-sticky-variant]');
    const price = this.querySelector('[data-ak-sticky-price]');
    if (!option || !button) return;
    button.disabled = option.dataset.available !== 'true';
    if (label) label.textContent = option.textContent.replace(' — Sold out', '').trim();
    this.#formatPrice(price, option.dataset.price);
  };

  #formatPrice = (element, value) => {
    if (!element || !Number.isFinite(Number(value))) return;
    element.textContent = new Intl.NumberFormat(this.dataset.locale || 'en-IN', {
      style: 'currency',
      currency: this.dataset.currency || 'INR',
      maximumFractionDigits: 2,
    }).format(Number(value) / 100);
  };

  #syncVisibility = () => {
    const hasCrossedMainButton = this.#mainButton?.getBoundingClientRect().bottom <= 0;
    const isVisible = Boolean(hasCrossedMainButton);

    this.classList.toggle('is-visible', isVisible);
    this.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

    // Reserve the exact fixed-bar height at the end of the page so the
    // footer credit can always be scrolled fully above it on every viewport.
    const stickyHeight = isVisible ? Math.ceil(this.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--ak-sticky-atc-height', `${stickyHeight}px`);

    if (!this.#footerSpacer?.isConnected) {
      const footer = document.querySelector('footer.footer, footer');
      if (footer) {
        this.#footerSpacer = document.createElement('div');
        this.#footerSpacer.setAttribute('data-ak-sticky-footer-spacer', '');
        this.#footerSpacer.setAttribute('aria-hidden', 'true');
        this.#footerSpacer.style.pointerEvents = 'none';
        footer.append(this.#footerSpacer);
      }
    }

    const footerClearance = isVisible ? stickyHeight + 16 : 0;
    if (this.#footerSpacer) this.#footerSpacer.style.height = `${footerClearance}px`;
  };

  #handleVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const button = this.querySelector('[data-ak-sticky-submit]');
    const variantLabel = this.querySelector('[data-ak-sticky-variant]');
    const price = this.querySelector('[data-ak-sticky-price]');
    const select = this.querySelector('[data-ak-sticky-variant-select]');

    if (!variant || !button) return;

    button.disabled = !variant.available;
    if (select && variant.id) select.value = String(variant.id);
    if (variantLabel) variantLabel.textContent = variant.public_title || variant.title || '';

    this.#formatPrice(price, variant.price);
  };
}

if (!customElements.get('ak-sticky-atc')) {
  customElements.define('ak-sticky-atc', AkStickyAtc);
}
